import { useCallback, useEffect, useState } from 'react';
import getCardPhotoUrl from '@alltrails/core/utils/getCardPhotoUrl';
import getCustomRoutePhotoUrl from '@alltrails/core/utils/getCustomRoutePhotoUrl';
import getPhotoSizeServerFromDimensions from '@alltrails/core/utils/getPhotoSizeServerFromDimensions';
import type CardType from '@alltrails/types/CardType';
import PhotoSizeDimensions from '@alltrails/types/PhotoSizeDimensions';

const addSizeToProfilePhoto = (profilePhoto: string, size: PhotoSizeDimensions): string => {
  const url = new URL(profilePhoto);
  url.searchParams.set('size', getPhotoSizeServerFromDimensions(size));
  return url.toString();
};

type Args = {
  baseUrl?: string;
  cardType: CardType;
  id: number;
  isCustomRoute?: boolean;
  maxPhotos: number;
  profilePhoto?: string;
  size: PhotoSizeDimensions;
};

const useCardPhotos = ({ baseUrl = '', cardType, id, isCustomRoute, maxPhotos, profilePhoto, size }: Args) => {
  const [photos, setPhotos] = useState<string[]>([]);
  const [photosLoaded, setPhotosLoaded] = useState(false);

  useEffect(() => {
    if (maxPhotos < 1) {
      setPhotos([]);
      setPhotosLoaded(true);
    } else {
      const fallbackPhotoUrl = isCustomRoute
        ? getCustomRoutePhotoUrl({ id, index: 0, size, baseUrl })
        : getCardPhotoUrl({ cardType, id, index: 0, size, baseUrl });
      const initialPhoto = profilePhoto ? addSizeToProfilePhoto(profilePhoto, size) : fallbackPhotoUrl;
      if (maxPhotos === 1) {
        setPhotos([initialPhoto]);
        setPhotosLoaded(true);
      } else {
        const fillerPhotos = Array(maxPhotos - 1).fill('') as string[];
        setPhotos([initialPhoto, ...fillerPhotos]);
        setPhotosLoaded(false);
      }
    }
  }, [baseUrl, cardType, id, isCustomRoute, maxPhotos, profilePhoto, size]);

  const loadPhotos = useCallback(() => {
    // For custom routes, we don't call loadPhotos as we only show one photo
    if (!photosLoaded && !isCustomRoute) {
      setPhotosLoaded(true);

      const urls = photos.map((_, index) => {
        const url = index === 0 && profilePhoto ? addSizeToProfilePhoto(profilePhoto, size) : getCardPhotoUrl({ cardType, id, index, size, baseUrl });
        // pre-load images by browser
        try {
          new Image().src = url;
        } catch {
          // silently catch
        }
        return url;
      });

      setPhotos(urls);
    }
  }, [baseUrl, cardType, id, isCustomRoute, photos, photosLoaded, profilePhoto, size]);

  return { photos, loadPhotos };
};

export default useCardPhotos;
